/**
 * Synchronously validates the syntax of a given URL.
 *
 * @param {string} url The URL to validate.
 * @returns {string|null} An error message if validation fails, otherwise null.
 */
export function validateUrl(url) {
    if (typeof url !== 'string') {
      return 'URL must be a string.';
    }

    const trimmedUrl = url.trim();

    // 1. Presence Checks
    if (!trimmedUrl) {
      return 'URL cannot be empty';
    }
    if (/\s/.test(trimmedUrl)) {
      return 'URL must not contain spaces';
    }

    // 2. Protocol Whitelisting
    if (trimmedUrl.startsWith('upi://') || trimmedUrl.startsWith('payto://')) {
      return null; // Valid payment link
    }
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      return 'URL must start with http:// or https://';
    }

    // 3. Domain & Host Structure + 4. Character Filtering
    try {
      const parsedUrl = new URL(trimmedUrl);
      const hostname = parsedUrl.hostname;

      // Host Existence (implicitly handled by new URL())
      if (!hostname) {
        // This case is rare as URL() would have already thrown an error
        return 'Invalid URL format: No hostname found.';
      }
      
      // TLD Requirement
      if (hostname.lastIndexOf('.') === -1) {
        return 'Must contain a TLD';
      }

      // TLD Validation
      const tld = hostname.substring(hostname.lastIndexOf('.') + 1);
      if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
        return 'Invalid top-level domain';
      }

      // Domain Characters validation
      const domainParts = hostname.split('.');
      for (const part of domainParts) {
        if (!/^[a-zA-Z0-9-]+$/.test(part)) {
          return 'Invalid domain characters';
        }
      }

      // Illegal Characters check for the whole URL
      // Characters to reject: < > ^ | ! @ ( ) { } [ ] \
      if (/[<>^|!@(){}[\]\\]/.test(trimmedUrl)) {
        return 'Invalid characters in URL';
      }

    } catch (e) {
      return 'Invalid URL format'; // Generic error for parsing failures
    }

    return null; // All checks passed
}
